/* eslint-disable react/destructuring-assignment */
import React, { type JSX } from 'react'
import { TargetsParam, animate } from 'animejs'
import { Transition } from 'react-transitioning'
import type { TransitionPhase, TransitionState } from 'react-transitioning'
import { layering } from '@mobi/component-library/Foundations'
import { observeImmutable } from '../HOCs'
import { OverlayStyled } from './Overlay.styles'
import { type OverlayState, type OverlayStateRecord, state$ } from './OverlayDriver'
import { OverlayReset } from './signals'

const ANIMATION_DURATION = 150
type AnimationDirection = 'entering' | 'exiting'
const ANIMATION_OPACITY: Record<AnimationDirection, readonly [number, number]> = {
  entering: [0, 1],
  exiting: [1, 0],
}
const ANIMATION_TRANSLATEY: Record<
  AnimationDirection,
  readonly [string | number, string | number]
> = {
  entering: ['3rem', 0],
  exiting: [0, '3rem'],
}

type ChildProps = {
  transitionState: TransitionState
  activePhase: TransitionPhase
  layer: typeof layering.overlayLow | typeof layering.overlayHigh | typeof layering.modalOverlayHigh
  children: React.ReactNode
}

class OverlayChild extends React.Component<ChildProps> {
  private overlayRef = React.createRef<HTMLDivElement>()

  public componentDidUpdate() {
    const { transitionState, activePhase } = this.props

    const isEntering = transitionState.enterActive || transitionState.appearActive
    const isExiting = activePhase === 'exit' && transitionState.exitActive

    if (isEntering || isExiting) {
      const animationDirection: AnimationDirection = isEntering ? 'entering' : 'exiting'
      const opacity = ANIMATION_OPACITY[animationDirection]
      const translateY = ANIMATION_TRANSLATEY[animationDirection]

      animate(this.overlayRef.current as TargetsParam, {
        opacity: {
          from: opacity[0],
          to: opacity[1],
          duration: ANIMATION_DURATION / 2,
        },
        translateY: {
          from: translateY[0],
          to: translateY[1],
        },
        ease: 'outQuart',
        duration: ANIMATION_DURATION,
        delay: 20,
      })
    }
  }

  public componentWillUnmount() {
    OverlayReset()
  }

  public render() {
    return (
      <OverlayStyled layer={this.props.layer} ref={this.overlayRef}>
        {this.props.children}
      </OverlayStyled>
    )
  }
}

export const OverlayComponent = ({ isOpen, Component, layer }: OverlayState): JSX.Element => (
  <Transition in={isOpen} duration={ANIMATION_DURATION}>
    {(transitionState, activePhase) => {
      return (
        <OverlayChild layer={layer} transitionState={transitionState} activePhase={activePhase}>
          {Component && !transitionState.exitDone && <Component />}
        </OverlayChild>
      )
    }}
  </Transition>
)

/** Used by Search */
export const Overlay = observeImmutable<OverlayStateRecord, object>(
  state$,
  ({ record: { Component, isOpen, layer } }) => (
    <OverlayComponent Component={Component} isOpen={isOpen} layer={layer} />
  )
)
